import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { useNovaObra, type NovaObraData, type ReportDefaults } from "@/hooks/useNovaObra";
import { UsuariosObraSection } from "./UsuariosObraSection";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { createObraInSupabase } from "@/services/obrasSupabaseService";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

// Interface for IBGE city response
interface IBGECity {
  id: number;
  nome: string;
}

// Hook for fetching cities from IBGE API
const useCities = (stateCode: string) => {
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!stateCode) {
      setCities([]);
      return;
    }

    const fetchCities = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(
          `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`
        );
        
        if (!response.ok) {
          throw new Error('Erro ao carregar cidades');
        }
        
        const data: IBGECity[] = await response.json();
        const cityNames = data.map(city => city.nome).sort();
        setCities(cityNames);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
        setCities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, [stateCode]);

  return { cities, loading, error };
};

// Fontes reais (Supabase): clientes do cadastro e usuários (profiles visíveis)
import { ClienteCache, clienteDisplayName } from "@/services/clienteCache";
import { supabase } from "@/integrations/supabase/client";

const statusOptions = [
  { value: "planejamento", label: "Planejamento" },
  { value: "em-andamento", label: "Em Andamento" },
  { value: "pausado", label: "Pausado" },
  { value: "concluido", label: "Concluído" },
  { value: "cancelado", label: "Cancelado" }
];

const estadosBrasileiros = [
  { sigla: "AC", nome: "Acre" },
  { sigla: "AL", nome: "Alagoas" },
  { sigla: "AP", nome: "Amapá" },
  { sigla: "AM", nome: "Amazonas" },
  { sigla: "BA", nome: "Bahia" },
  { sigla: "CE", nome: "Ceará" },
  { sigla: "DF", nome: "Distrito Federal" },
  { sigla: "ES", nome: "Espírito Santo" },
  { sigla: "GO", nome: "Goiás" },
  { sigla: "MA", nome: "Maranhão" },
  { sigla: "MT", nome: "Mato Grosso" },
  { sigla: "MS", nome: "Mato Grosso do Sul" },
  { sigla: "MG", nome: "Minas Gerais" },
  { sigla: "PA", nome: "Pará" },
  { sigla: "PB", nome: "Paraíba" },
  { sigla: "PR", nome: "Paraná" },
  { sigla: "PE", nome: "Pernambuco" },
  { sigla: "PI", nome: "Piauí" },
  { sigla: "RJ", nome: "Rio de Janeiro" },
  { sigla: "RN", nome: "Rio Grande do Norte" },
  { sigla: "RS", nome: "Rio Grande do Sul" },
  { sigla: "RO", nome: "Rondônia" },
  { sigla: "RR", nome: "Roraima" },
  { sigla: "SC", nome: "Santa Catarina" },
  { sigla: "SP", nome: "São Paulo" },
  { sigla: "SE", nome: "Sergipe" },
  { sigla: "TO", nome: "Tocantins" }
];

interface NovaObraModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (data: NovaObraData) => void;
}

export const NovaObraModal = ({ open, onOpenChange, onSubmit }: NovaObraModalProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    nomeObra,
    numeroObra,
    responsaveisObra,
    clienteObra,
    clientesResponsaveis,
    dataInicio,
    previsaoTermino,
    statusObra,
    endereco,
    reportDefaults,
    aprovacao,
    validationError,
    setNomeObra,
    setNumeroObra,
    setResponsaveisObra,
    setClienteObra,
    setClientesResponsaveis,
    setDataInicio,
    setPrevisaoTermino,
    setStatusObra,
    setEndereco,
    setReportDefaults,
    setAprovacao,
    resetForm,
    getFormData,
    validateForm
  } = useNovaObra();

  const [openCityCombo, setOpenCityCombo] = useState(false);
  const [showClienteField, setShowClienteField] = useState(false);
  const [saving, setSaving] = useState(false);

  // Clientes reais do cadastro (recalculado a cada abertura do modal)
  const clientesDisponiveis = useMemo(
    () => ClienteCache.getClientes().map((c) => ({ id: c.id, nome: clienteDisplayName(c), tipo: c.tipo })),
    [open]
  );

  // Usuários do sistema (profiles visíveis pela RLS; hoje, o usuário logado)
  const [usuariosDisponiveis, setUsuariosDisponiveis] = useState<{ id: string; nome: string }[]>([]);
  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data, error } = await supabase.from("profiles").select("id, full_name, email");
      if (error) { console.error("NovaObraModal profiles:", error.message); return; }
      setUsuariosDisponiveis((data ?? []).map((p) => ({ id: p.id, nome: p.full_name || p.email || "Usuário" })));
    })();
  }, [open]);

  // Use IBGE API for cities
  const { cities, loading: citiesLoading, error: citiesError } = useCities(endereco.estado);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const data = getFormData();

    if (!user) {
      toast({
        title: "Sessão expirada",
        description: "Faça login novamente para criar uma obra.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);
      const savedObra = await createObraInSupabase(data, user.id);
      // Atualiza o store em memória (getObraById/listObras síncronos) e
      // notifica o pai (ex.: refresh da lista) SOMENTE após salvar de verdade.
      const { hydrateObras } = await import("@/lib/obraCache");
      await hydrateObras();
      onSubmit?.(data);
      toast({
        title: "Obra criada",
        description: `Obra "${data.nomeObra}" foi salva com sucesso.`,
        action: (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/obras`)}
          >
            Abrir
          </Button>
        ),
      });
      resetForm();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Erro ao criar obra:", err);
      toast({
        title: "Erro ao criar obra",
        description: err.message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  const selectedCliente = clientesDisponiveis.find(c => c.id === clienteObra);

  const reportSections = [
    { key: 'condicoesClimaticas' as keyof ReportDefaults, label: 'Condições Climáticas' },
    { key: 'maoDeObra' as keyof ReportDefaults, label: 'Mão de Obra' },
    { key: 'equipamentos' as keyof ReportDefaults, label: 'Equipamentos' },
    { key: 'atividades' as keyof ReportDefaults, label: 'Atividades' },
    { key: 'ocorrencias' as keyof ReportDefaults, label: 'Ocorrências' },
    { key: 'materiais' as keyof ReportDefaults, label: 'Materiais' },
    { key: 'comentarios' as keyof ReportDefaults, label: 'Comentários' },
    { key: 'fotos' as keyof ReportDefaults, label: 'Fotos' },
    { key: 'videos' as keyof ReportDefaults, label: 'Vídeos' },
    { key: 'anexos' as keyof ReportDefaults, label: 'Anexos' },
  ];

  const handleReportDefaultToggle = (key: keyof ReportDefaults) => {
    setReportDefaults(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Obra</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Nome e Número da Obra */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-8 space-y-2">
              <Label htmlFor="nome-obra">Nome da obra</Label>
              <Input
                id="nome-obra"
                value={nomeObra}
                onChange={(e) => setNomeObra(e.target.value)}
                placeholder="Digite o nome da obra"
              />
            </div>
            
            <div className="col-span-4 space-y-2">
              <Label htmlFor="numero-obra">Número da obra</Label>
              <Input
                id="numero-obra"
                value={numeroObra}
                onChange={(e) => setNumeroObra(e.target.value)}
                placeholder="000"
              />
            </div>
          </div>

          {/* Responsáveis da Obra */}
          <div className="space-y-2">
            <Label htmlFor="responsaveis">Responsáveis da obra</Label>
            <Select 
              value={responsaveisObra.join(",")} 
              onValueChange={(value) => setResponsaveisObra(value ? [value] : [])}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione os responsáveis da obra" />
              </SelectTrigger>
              <SelectContent>
                {usuariosDisponiveis.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    Nenhum usuário disponível.
                  </div>
                ) : (
                  usuariosDisponiveis.map((usuario) => (
                    <SelectItem key={usuario.id} value={usuario.id}>
                      {usuario.nome}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Cliente */}
          <div className="space-y-2">
            <Label htmlFor="cliente">Cliente</Label>
            <Select 
              value={clienteObra} 
              onValueChange={(value) => {
                setClienteObra(value);
                setShowClienteField(!!value);
                setClientesResponsaveis([]);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o cliente" />
              </SelectTrigger>
              <SelectContent>
                {clientesDisponiveis.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground">
                    Nenhum cliente cadastrado. Cadastre em Cadastro → Clientes.
                  </div>
                ) : (
                  clientesDisponiveis.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome} ({cliente.tipo})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Cliente responsável */}
          {showClienteField && selectedCliente && (
            <div className="space-y-2">
              <Label htmlFor="cliente-responsavel">Cliente responsável</Label>
              <Select 
                value={clientesResponsaveis.join(",")} 
                onValueChange={(value) => setClientesResponsaveis(value ? [value] : [])}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o responsável do cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={selectedCliente.id}>
                    {selectedCliente.nome}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Datas */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dataInicio && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dataInicio ? format(dataInicio, "dd/MM/yyyy") : "Selecione a data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dataInicio}
                    onSelect={setDataInicio}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Previsão de término</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !previsaoTermino && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {previsaoTermino ? format(previsaoTermino, "dd/MM/yyyy") : "Selecione a data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={previsaoTermino}
                    onSelect={setPrevisaoTermino}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={statusObra} onValueChange={setStatusObra}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o status da obra" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Endereço */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Endereço</h3>
            
            {/* Rua + Número */}
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-8 space-y-1">
                <Label htmlFor="rua">Nome da rua</Label>
                <Input
                  id="rua"
                  value={endereco.rua}
                  onChange={(e) => setEndereco(prev => ({...prev, rua: e.target.value}))}
                  placeholder="Digite o nome da rua"
                />
              </div>
              
              <div className="col-span-4 space-y-1">
                <Label htmlFor="numero">Número</Label>
                <Input
                  id="numero"
                  value={endereco.numero}
                  onChange={(e) => setEndereco(prev => ({...prev, numero: e.target.value}))}
                  placeholder="Número"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="complemento">Complemento</Label>
              <Input
                id="complemento"
                value={endereco.complemento}
                onChange={(e) => setEndereco(prev => ({...prev, complemento: e.target.value}))}
                placeholder="Digite o complemento (opcional)"
              />
            </div>

            {/* Estado + Cidade */}
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-3 space-y-1">
                <Label htmlFor="estado">Estado</Label>
                <Select 
                  value={endereco.estado} 
                  onValueChange={(value) => {
                    setEndereco(prev => ({...prev, estado: value, cidade: ""}));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="UF" />
                  </SelectTrigger>
                  <SelectContent>
                    {estadosBrasileiros.map((estado) => (
                      <SelectItem key={estado.sigla} value={estado.sigla}>
                        {estado.sigla}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-9 space-y-1">
                <Label htmlFor="cidade">Cidade</Label>
                <Popover open={openCityCombo} onOpenChange={setOpenCityCombo}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openCityCombo}
                      className="w-full justify-between"
                      disabled={!endereco.estado || citiesLoading}
                    >
                      {endereco.cidade
                        ? endereco.cidade
                        : citiesLoading 
                          ? "Carregando cidades..." 
                          : citiesError 
                            ? "Erro ao carregar" 
                            : !endereco.estado 
                              ? "Selecione o estado primeiro" 
                              : "Selecione a cidade"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Digite para buscar..." />
                      <CommandList>
                        <CommandEmpty>Nenhuma cidade encontrada.</CommandEmpty>
                        <CommandGroup>
                          {cities.map((cidade) => (
                            <CommandItem
                              key={cidade}
                              value={cidade}
                              onSelect={(currentValue) => {
                                setEndereco(prev => ({...prev, cidade: currentValue}));
                                setOpenCityCombo(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  endereco.cidade === cidade ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {cidade}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {citiesError && (
                  <p className="text-xs text-destructive">
                    {citiesError}
                  </p>
                )}
              </div>
            </div>

            {/* CEP + Referência */}
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-4 space-y-1">
                <Label htmlFor="cep">CEP</Label>
                <Input
                  id="cep"
                  value={endereco.cep}
                  onChange={(e) => setEndereco(prev => ({...prev, cep: e.target.value}))}
                  placeholder="00000-000"
                />
              </div>
              
              <div className="col-span-8 space-y-1">
                <Label htmlFor="referencia">Referência</Label>
                <Input
                  id="referencia"
                  value={endereco.referencia}
                  onChange={(e) => setEndereco(prev => ({...prev, referencia: e.target.value}))}
                  placeholder="Digite uma referência (opcional)"
                />
              </div>
            </div>

            {/* Observação */}
            <div className="space-y-1">
              <Label htmlFor="observacao">Observação</Label>
              <Input
                id="observacao"
                value={endereco.observacao}
                onChange={(e) => setEndereco(prev => ({...prev, observacao: e.target.value}))}
                placeholder="Digite uma observação (opcional)"
              />
            </div>
          </div>

          {/* Padrão do Relatório */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Padrão do Relatório</h3>
            <p className="text-sm text-muted-foreground">
              Selecione quais seções devem estar disponíveis nos relatórios diários (RDO) desta obra.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {reportSections.map((section) => (
                <div key={section.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={section.key}
                    checked={reportDefaults[section.key]}
                    onCheckedChange={() => handleReportDefaultToggle(section.key)}
                    aria-label={`Incluir seção ${section.label} no relatório`}
                  />
                  <Label 
                    htmlFor={section.key}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {section.label}
                  </Label>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-muted-foreground">
              Pelo menos uma seção deve estar selecionada. Você pode alterar essas configurações posteriormente no painel da obra.
            </p>
          </div>

          {/* Seção de Usuários da Obra */}
          <div className="border-t pt-6">
            <UsuariosObraSection
              aprovacao={aprovacao}
              onAprovacaoChange={setAprovacao}
            />
          </div>

          {validationError && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={!nomeObra || saving}>
              {saving ? "Salvando..." : "Criar Obra"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};